import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';

import moment from 'moment-timezone';

import { getDefaultTimezone, getFormattedTimezone } from '@recruitee/time';

import { ButtonComponent } from '../../common/button/components/button/button.component';
import { ButtonDropdownIndicatorDirective } from '../../common/button/directives/button-dropdown/button-dropdown-indicator.directive';
import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../core/overlay/overlay-directives';
import { ConnectionPositionPair } from '../../core/overlay/position/connected-position';
import { InputComponent } from '../input/input.component';
import { TimeZonePickerDropdownComponent } from './time-zone-picker-dropdown/time-zone-picker-dropdown.component';

@Component({
  selector: 'rt-time-zone-picker',
  templateUrl: 'time-zone-picker.component.html',
  styleUrls: ['./time-zone-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.short]': 'short',
    '[class.small]': 'small',
  },
  standalone: true,
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    ConnectedOverlayDirective,
    IconColorDirective,
    IconComponent,
    InputComponent,
    NgIf,
    OverlayOriginDirective,
    TimeZonePickerDropdownComponent,
  ],
})
export class TimeZonePickerComponent {
  @Input() public short: boolean = false;
  @Input() public small: boolean = false;
  @Input() public timezone: string;
  @Input() public emit: boolean = false;
  @Input() public inputVariant: boolean = false;
  @Input() public hasMargin: boolean = true;
  @Input() public dropdownIconHasMargin: boolean = true;

  @Input() public dropdown: boolean = false;
  @Input() public dropdownMinWidth: number = 180;
  @Input() public positions: ConnectionPositionPair[] = [];

  @Output() public zoneChange: EventEmitter<string> = new EventEmitter();

  public timeZoneString: string;

  private currentTimeZone: string;

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngOnInit(): void {
    if (this.emit) {
      this.onZoneSelect(this.timezone || this.defaultTimezone());
    } else {
      this.setZone(this.timezone || this.defaultTimezone());
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { timezone } = changes;

    if (timezone && !timezone.firstChange) {
      this.setZone(this.timezone);
    }
  }

  public toggle(timezoneDropdown: ConnectedOverlayDirective): void {
    if (timezoneDropdown) {
      timezoneDropdown.open = !timezoneDropdown.open;
      this.cdRef.detectChanges();
    }
  }

  public onZoneSelect(zone: string, timezoneDropdown?: any): void {
    this.toggle(timezoneDropdown);
    this.setZone(zone);
    this.zoneChange.emit(zone);
  }

  private setZone(zone: string): void {
    this.currentTimeZone = zone;
    this.setTimeZoneString();
  }

  private defaultTimezone(): string {
    return getDefaultTimezone();
  }

  private setTimeZoneString(): void {
    const date = moment.tz(this.currentTimeZone);

    this.timeZoneString = this.short
      ? date.format('Z')
      : getFormattedTimezone(this.currentTimeZone);
    this.cdRef.detectChanges();
  }
}
